import React, { useState, useEffect } from 'react';
import { OPTION_LIST } from './static';
import { submitContactForm } from 'lib/hubspot';
import { useForm } from 'react-hook-form';
import LargeButton from '@/components/Views/Buttons';
import { LargeText } from '@/components/Views/Text';
import LargeHeading from '@/components/Views/Heading';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import { rudderTrack } from 'analytics/RudderstackProvider';
import * as AnalyticsEvents from 'analytics/events';
import Cookies from 'js-cookie';

export const ContactForm = () => {
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const {
        register,
        trigger,
        handleSubmit,
        watch,
        formState: { errors: formErrors }
    } = useForm();
    const watchPurpose = watch('contact_purpose', '');

    useEffect(() => {
        rudderTrack(AnalyticsEvents.ContactFormViewed, {
            page: 'Contact Us'
        });
    }, []);

    const validateName = (fullName: string): { valid: boolean; error?: string; parts?: string[] } => {
        if (!fullName || typeof fullName !== 'string') {
            return { valid: false, error: 'Name is required' };
        }
        const parts = fullName.trim().split(/\s+/).filter(Boolean);
        if (parts.length < 2) {
            return { valid: false, error: 'Please include both first and last name' };
        }
        const first = parts[0].toLowerCase();
        const last = parts[parts.length - 1].toLowerCase();
        if (first === last) {
            return { valid: false, error: 'suspicious', parts }; // Don't show error, just block
        }
        return { valid: true, parts };
    };

    const onSubmit = (data) => {
        setLoading(true);

        rudderTrack(AnalyticsEvents.ContactFormSubmitAttempted, {
            email: data.email,
            // eslint-disable-next-line camelcase
            contact_purpose: data.contact_purpose,
            nameLength: data.name?.length || 0,
            messageLength: data.message?.length || 0
        });

        // Check honeypot
        if (data.hp_field) {
            rudderTrack(AnalyticsEvents.ContactFormSpamBlocked, {
                reason: 'honeypot',
                email: data.email
            });
            setSubmitted(true);
            setLoading(false);
            return;
        }

        // Validate name
        const nameValidation = validateName(data?.name);
        if (!nameValidation.valid) {
            if (nameValidation.error === 'suspicious') {
                // Silent reject for identical names
                rudderTrack(AnalyticsEvents.ContactFormSpamBlocked, {
                    reason: 'identical_name',
                    email: data.email,
                    name: data.name
                });
                setSubmitted(true);
                setLoading(false);
                return;
            } else {
                // Show validation error for genuine issues
                rudderTrack(AnalyticsEvents.ContactFormValidationError, {
                    error: nameValidation.error,
                    field: 'name'
                });
                // eslint-disable-next-line no-alert
                alert(nameValidation.error);
                setLoading(false);
                return;
            }
        }

        console.log('submit', data);
        submitContactForm(data, 'Contact Us')
            .then(() => {
                rudderTrack(AnalyticsEvents.ContactFormSubmitSuccess, {
                    email: data.email,
                    // eslint-disable-next-line camelcase
                    contact_purpose: data.contact_purpose
                });
                setSubmitted(true);
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                rudderTrack(AnalyticsEvents.ContactFormSubmitError, {
                    email: data.email,
                    error: err?.message || 'Unknown error'
                });
                setLoading(false);
            });
    };

    return (
        <div id='contact-form' className=''>
            <LoadingOverlay className='fixed' show={loading} />
            <div className='contact-form'>

                {!submitted && (
                    <div className='contact-form--hero mb-10'>
                        <LargeHeading as='h1' className='lg:text-center font-bold' balanced={true}>
                            Say Hello
                        </LargeHeading>
                        <LargeText className='text-[#4B5563] text-center font-[600] mt-4'>
                            Send us a message below or email us at&nbsp;
                            <a href='mailto:info@muzology.com' target='_blank' rel='noreferrer'>info@muzology.com</a>.
                        </LargeText>
                        {/*
                        <h1>Say Hello</h1>
                        <NormalText className='text-center font-[500]'>
                            Send us a message below or email us at&nbsp;
                            <a href='mailto:info@muzology.com' target='_blank' rel='noreferrer'>info@muzology.com</a>.
                        </NormalText>
                        */}
                    </div>
                )}

                <div className='contact-form--wrapper md:px-[40px]'>


                    <div className='contact-form--content'>
                        <div className='contact-form--content-wrapper'>
                            {submitted ? (
                                <div className='contact-form--form'>
                                    <h3 className='contact-form--thanks'>Thanks for Contacting Us 🙂</h3>
                                </div>
                            ) : (
                                <div className='contact-form--form'>
                                    <form onSubmit={handleSubmit(onSubmit)} className='mt-0 flex flex-col gap-6'>
                                        <div className=''>
                                            <label className='block text-sm font-semibold text-gray-800'>Your Name</label>
                                            <input
                                                autoComplete='name'
                                                {...register('name', { required: true })}
                                                className='block w-full px-4 py-2 mt-2 text-[#6B7280] bg-white border-[#D1D5DB] border-[1.5px] border-solid rounded-md focus:border-[#5EC5DF] focus:ring-[#5EC5DF] focus:outline-none focus:ring focus:ring-opacity-40'
                                            />
                                            {formErrors.name && formErrors.name.type === 'required' && <div className='mt-1 text-red-500'>* This field is required</div>}
                                        </div>
                                        <div className=''>
                                            <label className='block text-sm font-semibold text-gray-800'>Your Email</label>
                                            <input
                                                type='email'
                                                autoComplete='email'
                                                {...register('email', { required: true })}
                                                className='block w-full px-4 py-2 mt-2 text-[#6B7280] bg-white border-[#D1D5DB] border-[1.5px] border-solid rounded-md focus:border-[#5EC5DF] focus:ring-[#5EC5DF] focus:outline-none focus:ring focus:ring-opacity-40'
                                            />
                                            {formErrors.email && formErrors.email.type === 'required' && <div className='mt-1 text-red-500'>* This field is required</div>}
                                        </div>
                                        <div className=''>
                                            <label className='block text-sm font-semibold text-gray-800'> What are you getting in touch about?</label>
                                            <select
                                                defaultValue=''
                                                className={`${watchPurpose === '' ? 'text-[#4f4f4f77]' : 'text-[#6B7280] font-[600]'} text-base block w-full px-4 py-2 mt-2 bg-white border-[#D1D5DB] border-[1.5px] border-solid rounded-md focus:border-[#5EC5DF] focus:ring-[#5EC5DF] focus:outline-none focus:ring focus:ring-opacity-40`}
                                                {...register('contact_purpose', { required: true })}
                                            >
                                                <option
                                                    disabled
                                                    value=''
                                                >
                                                    Select an option
                                                </option>
                                                {OPTION_LIST.map((item, index) => (
                                                    <option value={item} key={item}>
                                                        {item}
                                                    </option>
                                                ))}
                                            </select>
                                            {formErrors.contact_purpose && formErrors.contact_purpose.type === 'required' && <div className='mt-1 text-red-500'>* This field is required</div>}
                                        </div>
                                        <div className=''>
                                            <label className='block text-sm font-semibold text-gray-800'>Your Message</label>
                                            <textarea
                                                className='text-base block w-full px-4 py-2 mt-2 text-[#6B7280] bg-white border-[#D1D5DB] border-[1.5px] border-solid rounded-md focus:border-[#5EC5DF] focus:ring-[#5EC5DF] focus:outline-none focus:ring focus:ring-opacity-40'
                                                // placeholder='Your Message *'
                                                {...register('message', { required: true })}
                                                // OnChange={(e: any) => handleChange('message', e.target.value)}
                                                // name='message'
                                                // value={formData.message}
                                                rows={4}
                                            />
                                            {formErrors.message && formErrors.message.type === 'required' && <div className='mt-1 text-red-500'>* This field is required</div>}
                                        </div>
                                        {/* Honeypot field - hidden from humans, visible to bots */}
                                        <input
                                            type='text'
                                            autoComplete='off'
                                            tabIndex={-1}
                                            className='absolute opacity-0 pointer-events-none h-0 w-0'
                                            aria-hidden='true'
                                            {...register('hp_field')}
                                        />
                                        <div className=''>
                                            <div className='contact-form--row form-row-inputs'>
                                                <LargeButton text='Submit' className='blue-button mx-auto px-20' onClick={() => trigger()} />
                                            </div>
                                        </div>
                                    </form>
                                    {/*
                                    background: #ffffff;
                                    border: 1px solid #4f4f4f;
                                    border-radius: 100px;
                                    padding: 15px 35px;
                                    font-weight: 800;
                                    font-size: 18px;
                                    line-height: 20px;
                                    display: flex;
                                    align-items: center;
                                    color: #4f4f4f;
                                    width: 100%;
                                    <div className='contact-form--row form-row-inputs'>
                                        <div className={`contact-form--element ${formErrors.name ? 'error-element' : ''}`}>
                                            <input
                                                placeholder='Your Name *'
                                                {...register('name', { required: true })}
                                                // OnChange={(e: any) => handleChange('name', e.target.value)}
                                                // name='name'
                                                // value={formData.name}
                                            />
                                            <label className='contact-form--element-helper'>* This field is required</label>
                                        </div>
                                        <div className={`contact-form--element ${formErrors.email ? 'error-element' : ''}`}>
                                            <input
                                                placeholder='Your Email *'
                                                {...register('email', {
                                                    required: true,
                                                    validate: { validEmail: checkValidEmail }
                                                })}
                                            />
                                            <label className='contact-form--element-helper'>
                                                {formErrors.email?.type === 'required' && '* This field is required'}
                                                {formErrors.email?.type === 'validEmail' && '* This is not a valid email'}
                                            </label>
                                        </div>
                                    </div>
                                    <div className='contact-form--row form-row-inputs'>
                                        <div className={`contact-form--element ${formErrors.contact_purpose ? 'error-element' : ''}`}>
                                            <select
                                                defaultValue=''
                                                className={`${watchPurpose === '' ? 'placeholder-selected' : ''}`}
                                                {...register('contact_purpose', { required: true })}
                                            >
                                                <option disabled value=''>
                                                    What are you getting in touch about? *
                                                </option>
                                                {OPTION_LIST.map((item, index) => (
                                                    <option value={item} key={index}>
                                                        {item}
                                                    </option>
                                                ))}
                                            </select>
                                            <label className='contact-form--element-helper'>* This field is required</label>
                                        </div>
                                    </div>
                                    <div className='contact-form--row form-row-inputs'>
                                        <div className={`contact-form--element ${formErrors?.message ? 'error-element' : ''}`}>
                                            <textarea
                                                placeholder='Your Message *'
                                                {...register('message', {
                                                    required: true
                                                })}
                                                // OnChange={(e: any) => handleChange('message', e.target.value)}
                                                // name='message'
                                                // value={formData.message}
                                                rows={4}
                                            />
                                            <label className='contact-form--element-helper'>* This field is required</label>
                                        </div>
                                    </div>
                                    <div className='contact-form--row form-row-inputs'>
                                        <div className='contact-form--element'>
                                            <div className='contact-form--button'>
                                                <Button
                                                    style={{
                                                        top: '2px',
                                                        right: '2px',
                                                        width: '100%',
                                                        minWidth: '52px',
                                                        height: '45px'
                                                    }}
                                                    onClick={() => trigger()}
                                                >
                                                    SUBMIT
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    */}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <style jsx>
                {`
                    #contact-form {
                        position: relative;
                        width: 100%;
                        background-color: #fff;
                        //padding-top: 40px;
                        padding: 0 20px;

                        font-family: Inter, 'Inter', sans-serf, serif;
                        font-style: normal;
                        color: #131313;
                        //background-color: red;
                        //background-image: url('/assets/backgrounds/pricing-plan-bg.png'), url('/assets/backgrounds/howitworks-bg.png');
                        background-image: url('/assets/backgrounds/howitworks-bg.png');

                        background-repeat: no-repeat, no-repeat;
                        background-size: 100%, 100%;
                        background-position: 80% 100%, center;
                    }

                    .contact-form {
                        //background: #f9f9f9;
                        //background: white;
                        padding: 160px 40px 54px;

                        //background: orange;
                        .contact-form--hero {
                            h1 {
                                font-weight: 800;
                                font-size: 48px;
                                line-height: 55px;
                                text-align: center;
                                //background: -webkit-linear-gradient(135deg, #e637a8 2.69%, #e68137 75%);
                                color: #131313;
                                //-webkit-background-clip: text;
                                //-webkit-text-fill-color: transparent;
                                margin: 0;
                            }

                            p,
                            a {
                                font-weight: 500;
                                font-size: 18px;
                                line-height: 33px;
                                text-align: center;
                                color: #4f4f4f;
                                margin: 23px 0 0;
                            }
                        }

                        .contact-form--wrapper {
                            background: white;
                            //padding: 40px;
                            padding-top: 40px;
                            padding-bottom: 40px;
                            border-radius: 10px;
                            box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
                            position: relative;
                            z-index: 1;
                            max-width: 800px;
                            margin: 0 auto;


                            .contact-form--content {
                                max-width: 1533px;
                                margin: 0 auto;
                                //background-image: url('/assets/backgrounds/cu-fm.svg');
                                //background-size: contain;
                                //background-repeat: no-repeat;
                                //background-position: center;

                                .contact-form--content-wrapper {
                                    max-width: 1100px;
                                    margin: 0 auto;
                                    //background-image: url('/assets/graphics/ellipse-7.svg');
                                    //background-size: auto;
                                    //background-repeat: no-repeat;
                                    //background-position: left bottom;
                                    //padding: 105px 0 0;

                                    .contact-form--form {
                                        max-width: 937px;
                                        margin: 0 auto;

                                        .contact-form--row {
                                            display: flex;
                                            padding: 13px 0;
                                            justify-content: center;
                                        }

                                        .form-row-inputs {
                                            width: 100%;

                                            .contact-form--element {
                                                flex: 1;
                                                margin: 0 18px;

                                                &:first-child {
                                                    margin-left: 0;
                                                }

                                                &:last-child {
                                                    margin-right: 0;
                                                }

                                                input,
                                                select,
                                                textarea {
                                                    background: #ffffff;
                                                    border: 1px solid #4f4f4f;
                                                    border-radius: 100px;
                                                    padding: 15px 35px;
                                                    font-weight: 800;
                                                    font-size: 18px;
                                                    line-height: 20px;
                                                    display: flex;
                                                    align-items: center;
                                                    color: #4f4f4f;
                                                    width: 100%;

                                                    &::placeholder {
                                                        opacity: 0.5;
                                                        white-space: pre-line;
                                                        position: relative;
                                                    }
                                                }

                                                select {
                                                    -webkit-appearance: none;
                                                    -moz-appearance: none;
                                                    appearance: none;
                                                    outline: none;
                                                    background: white;
                                                    background-image: url('/assets/icons/chevron-down.svg');
                                                    background-repeat: no-repeat;
                                                    background-position-x: calc(100% - 20px);
                                                    background-position-y: 22px;

                                                    &.placeholder-selected {
                                                        color: #4f4f4f77;
                                                    }
                                                }

                                                textarea {
                                                    font-family: Avenir, 'Avenir', sans-serf;
                                                    border-radius: 20px;
                                                    -webkit-appearance: none;
                                                    -moz-appearance: none;
                                                    appearance: none;
                                                    outline: none;
                                                }

                                                .contact-form--element-helper {
                                                    display: none;
                                                }

                                                .contact-form--button {
                                                    width: 175px;
                                                    margin: 29px auto 0;
                                                }

                                                &.error-element {
                                                    input,
                                                    select {
                                                        border: 2px solid #df1848;
                                                    }

                                                    .contact-form--element-helper {
                                                        font-weight: 800;
                                                        font-size: 18px;
                                                        line-height: 25px;
                                                        display: flex;
                                                        align-items: center;
                                                        color: #df1848;
                                                        font-style: italic;
                                                        margin-top: 5px;
                                                    }
                                                }
                                            }
                                        }

                                        .contact-form--thanks {
                                            font-weight: 800;
                                            font-size: 42px;
                                            line-height: 48px;
                                            text-align: center;
                                            color: #121651;
                                            margin: 0;
                                            padding: 100px 0 200px;
                                        }
                                    }
                                }
                            }
                        }

                        @media (max-width: 992px) {
                            padding-top: 120px;
                        }

                        @media (max-width: 768px) {
                            padding: 120px 0 50px;

                            .contact-form--wrapper {
                                .contact-form--hero {
                                    h1 {
                                        font-size: 26px;
                                        line-height: 120%;
                                    }

                                    p,
                                    a {
                                        font-size: 18px;
                                        line-height: 120%;
                                        margin-top: 20px;
                                    }
                                }

                                .contact-form--content {
                                    .contact-form--content-wrapper {
                                        padding: 0px 20px 0;
                                        background-size: auto;
                                        background-position: center;

                                        .contact-form--form {
                                            .contact-form--row {
                                                flex-direction: column;
                                                padding: 0;
                                            }

                                            .form-row-inputs {
                                                .contact-form--element {
                                                    margin: 5px 0;

                                                    input,
                                                    select,
                                                    textarea {
                                                        font-size: 14px;
                                                        line-height: 19px;
                                                    }

                                                    .contact-form--button {
                                                        width: 100%;
                                                        margin-top: 20px;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                `}
            </style>
        </div>
    );
};
